import { colors } from '@kmns-internal/furikake'
import {
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ReportStatusEnum } from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { useState } from 'react'
import getCalculateScorePercentage from 'src/exShared/hooks/reports/getCalculateScorePercentage'
import { ReportResultSchema } from 'src/exShared/types/types'
import { getRecorderName } from 'src/exShared/util/report/getRecorderName'
import formatReportDate from 'src/exShared/util/reportResult/formatReportDate'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import type { WebReportResultProps } from 'src/features/reports/result/WebReportResultProps'

export const Overview: React.FC<WebReportResultProps> = (
  props: WebReportResultProps,
) => {
  const {
    report,
    isOffline,
    reportId,
    deviatedAnswers,
    allPagesChildNodes,
    addReportComment,
  } = props
  const { sumAllTotalScores, sumAllResultScores, calculateScorePercentage } =
    getCalculateScorePercentage(allPagesChildNodes(true))

  const reportName = report.name
  const userName = getRecorderName(report)

  const statusName = getStatusName(report.status.status)
  const { isFeatureAvailable } = useLimitedFeatureAvailability()

  const [newestComment, setNewestComment] = useState<string>(report.comment)
  const isCommentChanged = report.comment !== newestComment

  const onChangeText = (text: string) => {
    setNewestComment(text)
  }

  const onPressSaveButton = () => {
    addReportComment(reportId, newestComment)
  }

  const buildKaizenBadge = (improve: ReportResultSchema['improve']) => {
    if (!improve || !isFeatureAvailable('improves')) return undefined
    if ((improve?.indicatedIssueGroups?.length ?? 0) === 0) return undefined
    return <Chip label="カイゼン" color="primary" variant="pale" size="small" />
  }

  return (
    <Stack id="Overview" sx={{ flex: 1, width: '100%' }} spacing={1}>
      <Typography variant="h6">概要</Typography>
      <Paper elevation={1}>
        <Stack direction="row" sx={{ flex: '1 1 0' }}>
          <Stack
            spacing={1}
            sx={{
              minHeight: 160,
              width: '50%',
              p: 2,
              borderRight: `1px solid ${colors.divider.main}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6">{reportName}</Typography>
              {buildKaizenBadge(report.improve)}
            </Stack>
            <Stack spacing={0.5}>
              {report?.reportDate ? (
                <Typography variant="body2" color="text.secondary">
                  実施日：
                  {formatReportDate(report?.reportDate, report?.schedules)}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  実施なし
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                現場： {report?.place?.name ?? ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ユーザー：{userName}
              </Typography>
            </Stack>
          </Stack>
          <Stack sx={{ width: '50%' }}>
            <Stack sx={{ p: 2 }}>
              <Typography variant="subtitle2">スコア</Typography>
              <Stack
                sx={{ flex: 1, width: '100%', textAlign: 'center' }}
                justifyContent="center"
              >
                <Typography variant="h6">
                  {report.isShowScore
                    ? `${sumAllResultScores} / ${sumAllTotalScores} (${calculateScorePercentage}%)`
                    : 'ー'}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Stack direction="row" sx={{ flex: '1 1 0' }}>
              <Stack
                sx={{
                  flex: '1 1 0',
                  borderRight: `1px solid ${colors.divider.main}`,
                }}
              >
                <Stack
                  sx={{ height: 38, bgcolor: colors.background.grayLight }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="subtitle2" color={colors.text.primary}>
                    ステータス
                  </Typography>
                </Stack>
                <Stack
                  sx={{ height: 38 }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography>{statusName}</Typography>
                </Stack>
              </Stack>
              <Stack sx={{ flex: '1 1 0' }}>
                <Stack
                  sx={{ height: 38, bgcolor: colors.background.grayLight }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="subtitle2" color={colors.text.primary}>
                    逸脱項目
                  </Typography>
                </Stack>
                <Stack
                  sx={{ height: 38 }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      color: deviatedAnswers.length
                        ? colors.error.main
                        : colors.text.primary,
                    }}
                  >
                    {deviatedAnswers.length}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={1}>
          <Typography variant="subtitle2">コメント</Typography>
          <TextField
            multiline
            minRows={6}
            maxRows={30}
            onChange={e => onChangeText(e.target.value)}
            placeholder="コメントを入力"
            value={newestComment}
            aria-label="コメント"
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                color: colors.text.primary,
                fontSize: 16,
                fontWeight: 'bold',
                lineHeight: '20px',
                resize: 'none',
                textAlign: 'left',
              },
            }}
          />
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={onPressSaveButton}
          disabled={!isCommentChanged || isOffline}
        >
          保存
        </Button>
      </Stack>
    </Stack>
  )
}

const getStatusName = (status?: ReportStatusEnum) => {
  switch (status) {
    case ReportStatusEnum.INCOMPLETE:
      return '記録中'
    case ReportStatusEnum.COMPLETE:
      return '完了'
    case ReportStatusEnum.APPROVAL_PENDING:
      return '承認待ち'
    case ReportStatusEnum.APPROVAL_REMAND:
      return '差し戻し'
    default:
      return ''
  }
}
